import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Send, Server, CheckCircle2, AlertCircle } from "lucide-react";

const FLOWS = [
  { key: "register", label: "Registrierung", desc: "Begrüssungs-Mail an neuen Kunden", sender: "system" },
  { key: "intake-customer", label: "Intake (Kunde)", desc: "Bestätigung an Kunden nach Onboarding-Formular", sender: "system" },
  { key: "intake-admin", label: "Intake (Intern)", desc: "Benachrichtigung an office@mbw-swiss.ch", sender: "system" },
  { key: "contact-customer", label: "Kontakt (Kunde)", desc: "Bestätigung an Absender des Kontaktformulars", sender: "system" },
  { key: "contact-admin", label: "Kontakt (Intern)", desc: "Benachrichtigung an office@mbw-swiss.ch", sender: "system" },
  { key: "estimate", label: "Kostenvoranschlag", desc: "Angebot per Mail mit BCC an Office", sender: "offers" },
];

export default function AdminEmail() {
  const [recipient, setRecipient] = useState("office@mbw-swiss.ch");
  const [sender, setSender] = useState("system");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const sendTest = async () => {
    if (!recipient || !recipient.includes("@")) {
      toast.error("Bitte gültige E-Mail-Adresse angeben");
      return;
    }
    setBusy(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/admin/email/test", { recipient, sender });
      toast.success(`Test-Mail an ${data.recipient} versendet`);
      setLastResult({ ok: true, ...data, ts: new Date().toLocaleString("de-CH") });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Versand fehlgeschlagen";
      toast.error(msg);
      setLastResult({ ok: false, error: msg, ts: new Date().toLocaleString("de-CH") });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-email-page" className="space-y-6">
      <header>
        <h1 className="font-heading text-3xl text-[#1A2433] mb-1">E-Mail-Versand</h1>
        <p className="text-slate-600 text-sm">SMTP-Konfiguration (Hostpoint) prüfen und Test-Mails verschicken.</p>
      </header>

      {/* SMTP-Status */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Server className="w-5 h-5 text-[#991B1B]" />
          <h2 className="font-heading text-lg text-[#1A2433]">SMTP-Konfiguration</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <Row k="Server" v="asmtp.mail.hostpoint.ch" />
          <Row k="Port / Verschlüsselung" v="587 (STARTTLS)" />
          <Row k="System-Absender" v="system@mbw-swiss.ch" />
          <Row k="Offerten-Absender" v="offerten@mbw-swiss.ch" />
          <Row k="Reply-To" v="info@mbw-swiss.ch" />
          <Row k="Admin-Empfänger" v="office@mbw-swiss.ch" />
        </div>
      </div>

      {/* Test-Versand */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Send className="w-5 h-5 text-[#991B1B]" />
          <h2 className="font-heading text-lg text-[#1A2433]">Test-Mail versenden</h2>
        </div>

        <div className="grid sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
          <div>
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Empfänger</label>
            <input
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              className="mt-1 w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#991B1B]/30 text-sm"
              placeholder="empfaenger@example.ch"
              data-testid="email-test-recipient-input"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Absender</label>
            <select
              value={sender}
              onChange={(e) => setSender(e.target.value)}
              className="mt-1 px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#991B1B]/30 text-sm bg-white"
              data-testid="email-test-sender-select"
            >
              <option value="system">system@</option>
              <option value="offers">offerten@</option>
            </select>
          </div>
          <button
            onClick={sendTest}
            disabled={busy}
            className="bg-[#991B1B] hover:bg-[#7c1414] text-white font-medium px-6 py-2.5 rounded-lg disabled:opacity-50 transition-colors flex items-center gap-2 whitespace-nowrap"
            data-testid="email-test-send-btn"
          >
            <Mail className="w-4 h-4" />
            {busy ? "Sende…" : "Test senden"}
          </button>
        </div>

        {lastResult && (
          <div
            className={`mt-4 p-3 rounded-lg text-sm flex items-start gap-2 ${
              lastResult.ok ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                            : "bg-red-50 text-red-800 border border-red-200"
            }`}
            data-testid="email-test-result"
          >
            {lastResult.ok
              ? <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
              : <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
            <div>
              {lastResult.ok
                ? <>Mail an <strong>{lastResult.recipient}</strong> versendet (Absender: <strong>{lastResult.sender}</strong>) – {lastResult.ts}</>
                : <>Fehler: {lastResult.error} – {lastResult.ts}</>}
            </div>
          </div>
        )}
      </div>

      {/* Übersicht aller Flows */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="font-heading text-lg text-[#1A2433] mb-1">Aktive E-Mail-Flows</h2>
        <p className="text-sm text-slate-600 mb-4">Folgende automatischen Nachrichten werden vom System versendet:</p>
        <div className="space-y-2">
          {FLOWS.map((f) => (
            <div key={f.key} className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50">
              <div className={`w-2 h-2 mt-2 rounded-full flex-shrink-0 ${f.sender === "offers" ? "bg-[#DC2626]" : "bg-emerald-500"}`}></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-[#1A2433] text-sm">{f.label}</span>
                  <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                    von {f.sender === "offers" ? "offerten@" : "system@"}
                  </span>
                </div>
                <div className="text-xs text-slate-600 mt-0.5">{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-slate-100 last:border-0">
      <span className="text-slate-500">{k}</span>
      <span className="font-mono text-[#1A2433] text-[13px]">{v}</span>
    </div>
  );
}
